import React, { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  ChartLineUp,
  Database,
  HardDrives,
  ShieldCheck,
  Users,
  FileText,
  ShareNetwork,
  WarningCircle,
  Clock,
  Gauge,
} from "@phosphor-icons/react";
import { platform } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { SortableTableHeader } from "@/components/ui/SortableTableHeader";
import PlatformSupport from "@/components/platform/PlatformSupport";

/**
 * Metric card used across the SaaS Admin supervision dashboard.
 */
function MetricCard({ title, value, subtitle, icon: Icon }) {
  return (
    <div className="lirah-card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-eyebrow mb-2">{title}</div>
          <div className="font-heading text-2xl font-black text-[var(--do-ink)]">{value}</div>
          {subtitle && <div className="mt-1 text-xs text-[var(--do-muted)]">{subtitle}</div>}
        </div>
        {Icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
            <Icon size={20} weight="duotone" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Generic status badge.
 */
function StatusBadge({ value }) {
  const normalized = value || "unknown";
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{
        background:
          normalized === "active"
            ? "rgba(34,197,94,0.12)"
            : normalized === "suspended" || normalized === "disabled" || normalized === "past_due"
              ? "rgba(239,68,68,0.10)"
              : "rgba(148,163,184,0.14)",
        color: "var(--do-ink)",
        border: "1px solid var(--do-border)",
      }}
    >
      {normalized}
    </span>
  );
}

const SUPERVISION_SECTIONS = new Set([
  "global",
  "server",
  "security",
  "rgpd",
  "accounts",
  "support",
  "logs",
  "profile",
]);

/**
 * SaaS Admin supervision dashboard V2.
 *
 * This page is rendered inside the shared AppLayout.
 * PublicShare remains the only product page with its own application header.
 */
export default function Supervision() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [overview, setOverview] = useState(null);
  const [system, setSystem] = useState(null);
  const [security, setSecurity] = useState(null);
  const [rgpd, setRgpd] = useState(null);
  const [rgpdAdmin, setRgpdAdmin] = useState(null);
  const [selectedRgpdUserId, setSelectedRgpdUserId] = useState("");
  const [selectedRgpdTenantId, setSelectedRgpdTenantId] = useState("");
  const [rgpdProspects, setRgpdProspects] = useState([]);
  const [selectedRgpdProspectId, setSelectedRgpdProspectId] = useState("");
  const [accounts, setAccounts] = useState(null);
  const [logs, setLogs] = useState(null);

  const defaultTableFilters = {
    tenantsPage: 1,
    usersPage: 1,
    logsPage: 1,
    limit: 10,
    search: "",
    tenantId: "",
    role: "",
    status: "",
    action: "",
    dateFrom: "",
    dateTo: "",

    tenantsSortBy: "createdAt",
    tenantsSortOrder: "desc",

    usersSortBy: "createdAt",
    usersSortOrder: "desc",

    logsSortBy: "createdAt",
    logsSortOrder: "desc",
  };

  const [tableFilters, setTableFilters] = useState(
    () => ({ ...defaultTableFilters })
  );

  const [tenantsHistory, setTenantsHistory] = useState(null);
  const [usersHistory, setUsersHistory] = useState(null);
  const [logsHistory, setLogsHistory] = useState(null);

  const requestedSection =
    searchParams.get("section");

  const activeTab =
    SUPERVISION_SECTIONS.has(
      requestedSection
    )
      ? requestedSection
      : "global";
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");

  const shouldRedirect = user && user.role !== "saas_admin";

  const updateTableFilter = (key, value) => {
    setTableFilters((current) => {
      const next = {
        ...current,
        [key]: value,
      };

      // Page navigation must not reset filters or other pages.
      if (key === "tenantsPage" || key === "usersPage" || key === "logsPage") {
        return next;
      }

      // Changing any filter/search/sort/limit must restart each table from page 1.
      return {
        ...next,
        tenantsPage: 1,
        usersPage: 1,
        logsPage: 1,
      };
    });
  };

  const toggleTableSort = (
    tableKey,
    field
  ) => {
    const pageKeys = {
      tenants: "tenantsPage",
      users: "usersPage",
      logs: "logsPage",
    };

    const pageKey =
      pageKeys[tableKey];

    if (!pageKey) return;

    const sortByKey =
      `${tableKey}SortBy`;

    const sortOrderKey =
      `${tableKey}SortOrder`;

    setTableFilters((current) => ({
      ...current,
      [sortByKey]: field,
      [sortOrderKey]:
        current[sortByKey] === field &&
        current[sortOrderKey] === "asc"
          ? "desc"
          : "asc",
      [pageKey]: 1,
    }));
  };

  const exportLogsCsv = () => {
    const rows = logsHistory?.items || [];
    const header = ["createdAt", "action", "tenantId", "userId"];
    const csv = [
      header.join(";"),
      ...rows.map((item) =>
        header.map((key) => JSON.stringify(item?.[key] || "")).join(";")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `acta-platform-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const paginationLabel = (data) => `Page ${data?.page || 1}/${data?.totalPages || 1} · ${data?.total || 0} résultat(s)`;

  /**
   * Load only the paginated tables required by the active section.
   */
  const loadHistoryTables = async (tab = activeTab) => {
    const common = {
      limit: tableFilters.limit,
      search: tableFilters.search || undefined,
      status: tableFilters.status || undefined,
      dateFrom: tableFilters.dateFrom || undefined,
      dateTo: tableFilters.dateTo || undefined,
    };

    if (tab === "accounts") {
      const [tenantsData, usersData] = await Promise.all([
        platform.accountsPaginated({
          ...common,
          entity: "tenants",
          page: tableFilters.tenantsPage,
          sortBy: tableFilters.tenantsSortBy,
          sortOrder: tableFilters.tenantsSortOrder,
        }),
        platform.accountsPaginated({
          ...common,
          entity: "users",
          page: tableFilters.usersPage,
          sortBy: tableFilters.usersSortBy,
          sortOrder: tableFilters.usersSortOrder,
          tenantId: tableFilters.tenantId || undefined,
          role: tableFilters.role || undefined,
        }),
      ]);

      setTenantsHistory(tenantsData);
      setUsersHistory(usersData);
      return;
    }

    if (tab === "logs") {
      const logsData = await platform.logsPaginated({
        limit: tableFilters.limit,
        page: tableFilters.logsPage,
        search: tableFilters.search || undefined,
        tenantId: tableFilters.tenantId || undefined,
        action: tableFilters.action || undefined,
        dateFrom: tableFilters.dateFrom || undefined,
        dateTo: tableFilters.dateTo || undefined,
        sortBy: tableFilters.logsSortBy,
        sortOrder: tableFilters.logsSortOrder,
      });

      setLogsHistory(logsData);
    }
  };

  /**
   * Load only the data required by the active supervision section.
   */
  const loadData = async (tab = activeTab) => {
    setLoading(true);
    setError("");

    try {
      if (tab === "global") {
        setOverview(await platform.overview());
        return;
      }

      if (tab === "server") {
        setSystem(await platform.system());
        return;
      }

      if (tab === "security") {
        setSecurity(await platform.security());
        return;
      }

      if (tab === "rgpd") {
        const [
          rgpdData,
          rgpdAdminData,
          accountsData,
          prospectsData,
        ] = await Promise.all([
          platform.rgpd(),
          platform.rgpdAdmin({
            limit: 10,
            sortBy: "createdAt",
            sortOrder: "desc",
          }),
          platform.accounts(),
          platform.prospects({
            limit: 500,
            sortBy: "createdAt",
            sortOrder: "desc",
          }),
        ]);

        setRgpd(rgpdData);
        setRgpdAdmin(rgpdAdminData);
        setAccounts(accountsData);
        setRgpdProspects(prospectsData?.items || []);
        return;
      }

      if (tab === "accounts") {
        setAccounts(await platform.accounts());
        await loadHistoryTables(tab);
        return;
      }

      if (tab === "logs") {
        setLogs(await platform.logs({ limit: 100 }));
        await loadHistoryTables(tab);
      }
    } catch (err) {
      console.error("Supervision loadData error:", err);

      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Impossible de charger la supervision."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Changing section only loads that section.
   */
  useEffect(() => {
    loadData(activeTab);
  }, [activeTab]);

  /*
   * Filters only refresh the table displayed by the current section.
   */
  useEffect(() => {
    if (
      !loading &&
      ["accounts", "logs"].includes(activeTab)
    ) {
      loadHistoryTables(activeTab).catch((err) => {
        console.error(
          "Supervision history loading error:",
          err
        );
      });
    }
  }, [tableFilters]);

  /**
   * Run a protected action then refresh data.
   */
  const runAction = async (label, callback) => {
    if (!window.confirm(`${label} ?`)) return;

    setActionLoading(label);
    setError("");

    try {
      await callback();
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || "Action impossible.");
    } finally {
      setActionLoading("");
    }
  };

  const createUserFromPrompt = async () => {
    const tenantId = window.prompt("tenantId du nouvel utilisateur");
    if (!tenantId) return;

    const email = window.prompt("Email du nouvel utilisateur");
    if (!email) return;

    const role = window.prompt("Rôle : admin, manager, user", "user") || "user";
    const firstName = window.prompt("Prénom", "") || "";
    const lastName = window.prompt("Nom", "") || "";

    await runAction(`Créer l'utilisateur ${email}`, async () => {
      const result = await platform.createUser({ tenantId, email, role, firstName, lastName });
      if (result?.temporaryPassword) {
        window.alert(`Mot de passe temporaire à copier maintenant : ${result.temporaryPassword}`);
      }
    });
  };

  const editTenantFromPrompt = async (tenant) => {
    const name = window.prompt("Nom du tenant", tenant.name || "");
    if (!name) return;

    await runAction(`Modifier le tenant ${tenant.name}`, () =>
      platform.updateTenant(tenant.id, { name })
    );
  };

  const editUserFromPrompt = async (item) => {
    const firstName = window.prompt("Prénom", item.firstName || "");
    if (firstName === null) return;

    const lastName = window.prompt("Nom", item.lastName || "");
    if (lastName === null) return;

    const phone = window.prompt("WhatsApp professionnel", item.phone || "");
    if (phone === null) return;

    const title = window.prompt("Fonction", item.title || "");
    if (title === null) return;

    await runAction(`Modifier ${item.email}`, () =>
      platform.updateUser(item.id, { firstName, lastName, phone, title })
    );
  };

  const deleteUserFromPrompt = async (item) => {
    const reason = window.prompt(`Motif de suppression classique pour ${item.email}, minimum 10 caractères`);
    if (!reason || reason.trim().length < 10) {
      window.alert("Motif obligatoire.");
      return;
    }

    const confirm = window.prompt("Tape exactement DELETE_USER pour confirmer");
    if (confirm !== "DELETE_USER") {
      window.alert("Suppression annulée.");
      return;
    }

    await runAction(`Supprimer l'utilisateur ${item.email}`, () =>
      platform.deleteUser(item.id, { reason, confirm })
    );
  };

  const resetPasswordFromPrompt = async (item) => {
    await runAction(`Réinitialiser le mot de passe de ${item.email}`, async () => {
      const result = await platform.resetUserPassword(item.id);
      if (result?.temporaryPassword) {
        window.alert(`Mot de passe temporaire à copier maintenant : ${result.temporaryPassword}`);
      }
    });
  };

  const exportJson = (filename, payload) => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportRgpdUserFromSelection = async () => {
    if (!selectedRgpdUserId) {
      window.alert("Sélectionne d'abord un utilisateur.");
      return;
    }

    await runAction(`Exporter les données RGPD utilisateur`, async () => {
      const result = await platform.exportRgpdUser(selectedRgpdUserId);
      exportJson(`rgpd-user-${selectedRgpdUserId}.json`, result.export);
    });
  };

  const exportRgpdTenantFromSelection = async () => {
    if (!selectedRgpdTenantId) {
      window.alert("Sélectionne d'abord un tenant.");
      return;
    }

    await runAction(`Exporter l'inventaire RGPD tenant`, async () => {
      const result = await platform.exportRgpdTenant(selectedRgpdTenantId);
      exportJson(`rgpd-tenant-${selectedRgpdTenantId}.json`, result.export);
    });
  };

  const anonymizeRgpdUserFromSelection = async () => {
    if (!selectedRgpdUserId) {
      window.alert("Sélectionne d'abord un utilisateur.");
      return;
    }

    const reason = window.prompt(
      "Motif RGPD d'anonymisation, minimum 10 caractères"
    );

    if (!reason || reason.trim().length < 10) {
      window.alert("Motif obligatoire.");
      return;
    }

    const confirm = window.prompt(
      "Tape exactement ANONYMISER pour confirmer"
    );

    if (confirm !== "ANONYMISER") {
      window.alert("Anonymisation annulée.");
      return;
    }

    await runAction(
      "Anonymiser l'utilisateur sélectionné",
      () =>
        platform.anonymizeRgpdUser(
          selectedRgpdUserId,
          { reason, confirm }
        )
    );

    setSelectedRgpdUserId("");
  };

  const deleteRgpdUserFromSelection = async () => {
    if (!selectedRgpdUserId) {
      window.alert("Sélectionne d'abord un utilisateur.");
      return;
    }

    const reason = window.prompt("Motif RGPD de suppression, minimum 10 caractères");
    if (!reason || reason.trim().length < 10) {
      window.alert("Motif obligatoire.");
      return;
    }

    const confirm = window.prompt('Tape exactement SUPPRIMER pour confirmer');
    if (confirm !== "SUPPRIMER") {
      window.alert("Suppression annulée.");
      return;
    }

    await runAction("Supprimer cet utilisateur sur demande RGPD", () =>
      platform.deleteRgpdUser(selectedRgpdUserId, { reason, confirm })
    );

    setSelectedRgpdUserId("");
  };

  const anonymizeRgpdProspectFromSelection = async () => {
    if (!selectedRgpdProspectId) {
      window.alert("Sélectionne d'abord un prospect.");
      return;
    }

    const reason = window.prompt(
      "Motif RGPD d'anonymisation du prospect, minimum 10 caractères"
    );

    if (!reason || reason.trim().length < 10) {
      window.alert("Motif obligatoire.");
      return;
    }

    const confirm = window.prompt(
      "Tape exactement ANONYMISER pour confirmer"
    );

    if (confirm !== "ANONYMISER") {
      window.alert("Anonymisation annulée.");
      return;
    }

    await runAction(
      "Anonymiser le prospect sélectionné",
      () =>
        platform.anonymizeRgpdProspect(
          selectedRgpdProspectId,
          { reason, confirm }
        )
    );

    setSelectedRgpdProspectId("");
  };

  const exportRgpdProspectFromSelection = async () => {
    if (!selectedRgpdProspectId) {
      window.alert("Sélectionne d'abord un prospect.");
      return;
    }

    await runAction("Exporter les données RGPD du prospect", async () => {
      const result = await platform.exportRgpdProspect(selectedRgpdProspectId);
      exportJson(`rgpd-prospect-${selectedRgpdProspectId}.json`, result.export);
    });
  };

  const deleteProspectFromSelection = async () => {
    if (!selectedRgpdProspectId) {
      window.alert("Sélectionne d'abord un prospect.");
      return;
    }

    const reason = window.prompt("Motif de suppression classique du prospect, minimum 10 caractères");
    if (!reason || reason.trim().length < 10) {
      window.alert("Motif obligatoire.");
      return;
    }

    const confirm = window.prompt("Tape exactement DELETE_PROSPECT pour confirmer");
    if (confirm !== "DELETE_PROSPECT") {
      window.alert("Suppression annulée.");
      return;
    }

    await runAction("Supprimer ce prospect", () =>
      platform.deleteProspect(selectedRgpdProspectId, { reason, confirm })
    );

    setSelectedRgpdProspectId("");
  };

  const deleteRgpdProspectFromSelection = async () => {
    if (!selectedRgpdProspectId) {
      window.alert("Sélectionne d'abord un prospect.");
      return;
    }

    const reason = window.prompt("Motif RGPD de suppression du prospect, minimum 10 caractères");
    if (!reason || reason.trim().length < 10) {
      window.alert("Motif obligatoire.");
      return;
    }

    const confirm = window.prompt("Tape exactement SUPPRIMER pour confirmer");
    if (confirm !== "SUPPRIMER") {
      window.alert("Suppression annulée.");
      return;
    }

    await runAction("Supprimer ce prospect sur demande RGPD", () =>
      platform.deleteRgpdProspect(selectedRgpdProspectId, { reason, confirm })
    );

    setSelectedRgpdProspectId("");
  };

  const resetTenantStatsFromTenant = async (tenantId, tenantName) => {
    const reason = window.prompt(`Motif de remise à zéro des statistiques pour ${tenantName || tenantId}, minimum 10 caractères`);
    if (!reason || reason.trim().length < 10) {
      window.alert("Motif obligatoire.");
      return;
    }

    const confirm = window.prompt('Tape exactement RESET_STATS pour confirmer');
    if (confirm !== "RESET_STATS") {
      window.alert("Remise à zéro annulée.");
      return;
    }

    await runAction("Remettre à zéro les statistiques du tenant", () =>
      platform.resetTenantStats(tenantId, { reason, confirm })
    );
  };

  const updateRetentionFromPrompt = async () => {
    const auditLogsMonths = window.prompt("Conservation audit logs en mois", rgpdAdmin?.retention?.auditLogsMonths || 12);
    if (!auditLogsMonths) return;

    const prospectsInactiveMonths = window.prompt("Conservation prospects inactifs en mois", rgpdAdmin?.retention?.prospectsInactiveMonths || 36);
    if (!prospectsInactiveMonths) return;

    const expiredSharesMonths = window.prompt("Conservation partages expirés en mois", rgpdAdmin?.retention?.expiredSharesMonths || 12);
    if (!expiredSharesMonths) return;

    const rgpdRequestsYears = window.prompt("Conservation demandes RGPD en années", rgpdAdmin?.retention?.rgpdRequestsYears || 5);
    if (!rgpdRequestsYears) return;

    await runAction("Mettre à jour les durées de conservation", () =>
      platform.updateRgpdRetention({
        auditLogsMonths,
        prospectsInactiveMonths,
        expiredSharesMonths,
        rgpdRequestsYears,
      })
    );
  };

  const counters = overview?.counters || {};
  const services = overview?.services || {};
  const accountSummary = accounts?.summary || {};

  if (shouldRedirect) {
    return <Navigate to="/monpremiermonitoring.fr" replace />;
  }

  const HistoryFilters = ({ section }) => (
    <div
      className="rounded-[18px] p-4"
      style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}
      data-testid={`history-filters-${section}`}
    >
      <div className="grid md:grid-cols-2 xl:grid-cols-6 gap-3">
        <input
          value={tableFilters.search}
          onChange={(event) => updateTableFilter("search", event.target.value)}
          placeholder="Recherche"
          className="px-3 py-2 rounded-[10px] border text-sm"
          style={{ background: "var(--do-bg-solid)", borderColor: "var(--do-border)", color: "var(--do-ink)" }}
        />

        <input
          value={tableFilters.tenantId}
          onChange={(event) => updateTableFilter("tenantId", event.target.value)}
          placeholder="tenantId"
          className="px-3 py-2 rounded-[10px] border text-sm"
          style={{ background: "var(--do-bg-solid)", borderColor: "var(--do-border)", color: "var(--do-ink)" }}
        />

        <input
          type="date"
          value={tableFilters.dateFrom}
          onChange={(event) => updateTableFilter("dateFrom", event.target.value)}
          className="px-3 py-2 rounded-[10px] border text-sm"
          style={{ background: "var(--do-bg-solid)", borderColor: "var(--do-border)", color: "var(--do-ink)" }}
        />

        <input
          type="date"
          value={tableFilters.dateTo}
          onChange={(event) => updateTableFilter("dateTo", event.target.value)}
          className="px-3 py-2 rounded-[10px] border text-sm"
          style={{ background: "var(--do-bg-solid)", borderColor: "var(--do-border)", color: "var(--do-ink)" }}
        />

        <select
          value={tableFilters.limit}
          onChange={(event) => updateTableFilter("limit", Number(event.target.value))}
          className="px-3 py-2 rounded-[10px] border text-sm"
          style={{ background: "var(--do-bg-solid)", borderColor: "var(--do-border)", color: "var(--do-ink)" }}
        >
          <option value={10}>10 / page</option>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
        </select>

        <button
          type="button"
          onClick={() =>
            setTableFilters({ ...defaultTableFilters })
          }
          className="px-3 py-2 rounded-[10px] text-sm font-semibold"
          style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)", color: "var(--do-ink)" }}
        >
          Réinitialiser
        </button>
      </div>
    </div>
  );

  const PaginationControls = ({ data, pageKey }) => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-3" style={{ borderTop: "1px solid var(--do-border)" }}>
      <div className="text-xs" style={{ color: "var(--do-muted)" }}>{paginationLabel(data)}</div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={(data?.page || 1) <= 1}
          onClick={() => updateTableFilter(pageKey, Math.max(1, (data?.page || 1) - 1))}
          className="px-3 py-1.5 rounded-[8px] text-xs font-semibold disabled:opacity-40"
          style={{ border: "1px solid var(--do-border)" }}
        >
          Précédent
        </button>
        <button
          type="button"
          disabled={(data?.page || 1) >= (data?.totalPages || 1)}
          onClick={() => updateTableFilter(pageKey, (data?.page || 1) + 1)}
          className="px-3 py-1.5 rounded-[8px] text-xs font-semibold disabled:opacity-40"
          style={{ border: "1px solid var(--do-border)" }}
        >
          Suivant
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="label-eyebrow lirah-pulse">Chargement supervision V2</div>
      </div>
    );
  }

  return (
    <main
      className="acta-standard-page w-full min-w-0 max-w-full p-4 sm:p-5 lg:p-8"
      data-testid="supervision-page"
    >
      {/* Le header principal est maintenant fourni par AppLayout. */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="label-eyebrow mb-1">
            SaaS Admin Platform
          </div>

          <h1
            className="font-heading text-2xl font-bold"
            style={{ color: "var(--do-ink)" }}
          >
            Supervision Acta Documentum
          </h1>

          <div
            className="mt-1 text-xs"
            style={{ color: "var(--do-muted)" }}
          >
            Connecté : {user?.email || "—"}
          </div>
        </div>

        <button
          type="button"
          onClick={loadData}
          className="self-start inline-flex h-9 items-center rounded-lg bg-[var(--do-blue)] px-4 text-xs font-heading font-bold text-[var(--do-on-accent)] sm:self-auto"
          data-testid="supervision-refresh"
        >
          Actualiser
        </button>
      </div>

      <section className="space-y-6">
        {error && (
          <div
            className="rounded-[14px] p-4 flex items-start gap-3"
            style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}
            data-testid="supervision-error"
          >
            <WarningCircle size={20} weight="bold" />
            <div className="text-sm">{error}</div>
          </div>
        )}

        {actionLoading && (
          <div className="label-eyebrow lirah-pulse">Action en cours : {actionLoading}</div>
        )}

        {activeTab === "global" && (
          <section className="space-y-6">
            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <MetricCard title="Tenants" value={counters.tenants ?? "—"} icon={Database} />
              <MetricCard title="Utilisateurs" value={counters.users ?? "—"} icon={Users} />
              <MetricCard title="Documents" value={counters.documents ?? "—"} icon={FileText} />
              <MetricCard title="Partages" value={counters.shares ?? "—"} icon={ShareNetwork} />
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <MetricCard title="Prospects" value={counters.prospects ?? "—"} icon={Users} />
              <MetricCard title="Audit logs" value={counters.auditLogs ?? "—"} icon={ShieldCheck} />
              <MetricCard title="Backend" value={services.backend || "—"} subtitle="Service API" icon={ChartLineUp} />
              <MetricCard title="Base de données" value={services.database || "—"} subtitle="MongoDB" icon={Database} />
            </div>
          </section>
        )}

        {activeTab === "server" && (
          <section className="space-y-6">
            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <MetricCard title="CPU" value={`${system?.cpu?.loadPercentApprox ?? "—"} %`} subtitle={`${system?.cpu?.cpuCount ?? "—"} cœurs`} icon={Gauge} />
              <MetricCard title="RAM utilisée" value={`${system?.memory?.usedPercent ?? "—"} %`} subtitle={`${system?.memory?.usedMb ?? "—"} / ${system?.memory?.totalMb ?? "—"} MB`} icon={ChartLineUp} />
              <MetricCard title="Disque utilisé" value={`${system?.disk?.usedPercent ?? "—"} %`} subtitle={`${system?.disk?.usedGb ?? "—"} / ${system?.disk?.totalGb ?? "—"} GB`} icon={HardDrives} />
              <MetricCard title="Uptime" value={system?.uptime?.human || "—"} subtitle={`${system?.uptime?.seconds ?? "—"} secondes`} icon={Clock} />
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <MetricCard title="Uploads" value={`${system?.uploads?.sizeGb ?? 0} GB`} subtitle={system?.uploads?.path || "UPLOAD_DIR"} icon={FileText} />
              <MetricCard title="Docker" value={system?.docker?.available ? `${system?.docker?.count} containers` : "Non lisible"} subtitle="Statut containers" icon={Database} />
              <MetricCard title="Sauvegarde" value={system?.backup?.status || "missing"} subtitle={system?.backup?.latestAgeHours ? `${system.backup.latestAgeHours}h` : "Aucune sauvegarde détectée"} icon={HardDrives} />
              <MetricCard title="Nginx logs" value={system?.nginx?.available ? "Lisible" : "Non monté"} subtitle="Erreurs récentes" icon={ShieldCheck} />
            </div>

            <div
              className="rounded-[18px] p-5"
              style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}
            >
              <div className="label-eyebrow mb-3">Alertes plateforme</div>
              <div className="space-y-2">
                {(system?.alerts || []).map((alert, index) => (
                  <div key={`${alert.level}-${index}`} className="text-sm" style={{ color: "var(--do-ink)" }}>
                    <strong>{alert.level}</strong> — {alert.message}
                  </div>
                ))}
              </div>
            </div>

            <div
              className="rounded-[18px] p-5"
              style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}
            >
              <div className="label-eyebrow mb-3">Containers Docker</div>
              {system?.docker?.containers?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ color: "var(--do-muted)", borderBottom: "1px solid var(--do-border)" }}>
                        <th className="text-left py-2 pr-4">Nom</th>
                        <th className="text-left py-2 pr-4">Image</th>
                        <th className="text-left py-2 pr-4">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {system.docker.containers.map((container) => (
                        <tr key={container.name} style={{ borderBottom: "1px solid var(--do-border)" }}>
                          <td className="py-3 pr-4 font-semibold">{container.name}</td>
                          <td className="py-3 pr-4 font-mono text-xs">{container.image}</td>
                          <td className="py-3 pr-4">{container.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm" style={{ color: "var(--do-muted)" }}>
                  Docker n'est pas encore lisible depuis le backend. Il faudra monter le socket Docker en lecture contrôlée ou passer par un exporter.
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === "security" && (
          <section className="space-y-4">
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              <MetricCard title="Audit logs" value={security?.auditLogsCount ?? "—"} icon={ShieldCheck} />
              <MetricCard title="Tokens publics actifs" value="À connecter" subtitle="Étape suivante : shares actifs/expirés/révoqués" icon={ShareNetwork} />
              <MetricCard title="Isolation tenant" value="À auditer" subtitle="Vérification automatique à ajouter" icon={Database} />
            </div>

            <div className="rounded-[18px] p-5" style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}>
              <div className="label-eyebrow mb-3">Événements sécurité récents</div>
              {(security?.recentAuditLogs || []).map((item) => (
                <div key={item.id || item.createdAt} className="py-3" style={{ borderBottom: "1px solid var(--do-border)" }}>
                  <div className="text-sm font-semibold" style={{ color: "var(--do-ink)" }}>{item.action}</div>
                  <div className="text-xs" style={{ color: "var(--do-muted)" }}>
                    {item.tenantId} · {item.userId} · {item.createdAt}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {activeTab === "rgpd" && (
          <section className="space-y-6">
            <div className="grid sm:grid-cols-2 xl:grid-cols-6 gap-4">
              <MetricCard title="Demandes ouvertes" value={rgpdAdmin?.summary?.openRequests ?? 0} icon={FileText} />
              <MetricCard title="Exports RGPD" value={rgpdAdmin?.summary?.exportsCount ?? 0} icon={FileText} />
              <MetricCard title="Anonymisations" value={rgpdAdmin?.summary?.anonymisationsCount ?? 0} icon={Users} />
              <MetricCard title="Actions sensibles" value={rgpdAdmin?.summary?.sensitiveActionsCount ?? 0} icon={ShieldCheck} />
              <MetricCard title="Données à purger" value={rgpdAdmin?.summary?.dataToPurge ?? 0} icon={WarningCircle} />
              <MetricCard title="Dernier audit" value={rgpdAdmin?.summary?.lastAuditAt || "—"} icon={Clock} />
            </div>

            <div
              className="rounded-[18px] p-5 space-y-4"
              style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}
            >
              <div className="label-eyebrow">Exports et anonymisations RGPD</div>

              <div className="grid xl:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="label-eyebrow">Utilisateur</label>
                  <select
                    value={selectedRgpdUserId}
                    onChange={(event) => setSelectedRgpdUserId(event.target.value)}
                    className="w-full px-3 py-2 rounded-[10px] border text-sm"
                    style={{ background: "var(--do-bg-solid)", borderColor: "var(--do-border)", color: "var(--do-ink)" }}
                    data-testid="rgpd-user-select"
                  >
                    <option value="">Sélectionner un utilisateur</option>
                    {(accounts?.users || []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.email} — {item.firstName} {item.lastName} — {item.tenantId}
                      </option>
                    ))}
                  </select>

                  <div className="flex flex-wrap gap-2">

                    <button type="button" onClick={exportRgpdUserFromSelection} className="px-4 py-2 rounded-[10px] text-sm font-semibold" style={{ background: "linear-gradient(135deg, var(--do-blue), var(--do-navy))", color: "#FFFFFF" }}>
                      Exporter utilisateur
                    </button>
                    <button type="button" onClick={anonymizeRgpdUserFromSelection} className="px-4 py-2 rounded-[10px] text-sm font-semibold" style={{ background: "var(--do-bg-solid)", color: "var(--do-ink)", border: "1px solid var(--do-border)" }}>
                      Anonymiser utilisateur
                    </button>
                    <button type="button" onClick={deleteRgpdUserFromSelection} className="px-4 py-2 rounded-[10px] text-sm font-semibold" style={{ background: "rgba(239,68,68,0.12)", color: "var(--do-ink)", border: "1px solid rgba(239,68,68,0.35)" }}>
                      Supprimer RGPD
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="label-eyebrow">Tenant</label>
                  <select
                    value={selectedRgpdTenantId}
                    onChange={(event) => setSelectedRgpdTenantId(event.target.value)}
                    className="w-full px-3 py-2 rounded-[10px] border text-sm"
                    style={{ background: "var(--do-bg-solid)", borderColor: "var(--do-border)", color: "var(--do-ink)" }}
                    data-testid="rgpd-tenant-select"
                  >
                    <option value="">Sélectionner un tenant</option>
                    {(accounts?.tenants || []).map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name} — {tenant.status}
                      </option>
                    ))}
                  </select>

                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={exportRgpdTenantFromSelection} className="px-4 py-2 rounded-[10px] text-sm font-semibold" style={{ background: "linear-gradient(135deg, var(--do-blue), var(--do-navy))", color: "#FFFFFF" }}>
                      Exporter tenant
                    </button>
                    <button type="button" onClick={updateRetentionFromPrompt} className="px-4 py-2 rounded-[10px] text-sm font-semibold" style={{ background: "var(--do-bg-solid)", color: "var(--do-ink)", border: "1px solid var(--do-border)" }}>
                      Conservation
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="label-eyebrow">Prospect</label>
                <select
                  value={selectedRgpdProspectId}
                  onChange={(event) => setSelectedRgpdProspectId(event.target.value)}
                  className="w-full px-3 py-2 rounded-[10px] border text-sm"
                  style={{ background: "var(--do-bg-solid)", borderColor: "var(--do-border)", color: "var(--do-ink)" }}
                  data-testid="rgpd-prospect-select"
                >
                  <option value="">Sélectionner un prospect</option>
                  {rgpdProspects.map((prospect) => (
                    <option key={prospect.id} value={prospect.id}>
                      {prospect.firstName} {prospect.lastName} — {prospect.email || prospect.phone || "sans contact"} — {prospect.tenantId}
                    </option>
                  ))}
                </select>

                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={exportRgpdProspectFromSelection} className="px-4 py-2 rounded-[10px] text-sm font-semibold" style={{ background: "linear-gradient(135deg, var(--do-blue), var(--do-navy))", color: "#FFFFFF" }}>
                    Exporter prospect
                  </button>
                  <button type="button" onClick={anonymizeRgpdProspectFromSelection} className="px-4 py-2 rounded-[10px] text-sm font-semibold" style={{ background: "var(--do-bg-solid)", color: "var(--do-ink)", border: "1px solid var(--do-border)" }}>
                    Anonymiser prospect
                  </button>
                  <button type="button" onClick={deleteProspectFromSelection} className="px-4 py-2 rounded-[10px] text-sm font-semibold" style={{ background: "rgba(239,68,68,0.08)", color: "var(--do-ink)", border: "1px solid rgba(239,68,68,0.25)" }}>
                    Supprimer
                  </button>
                  <button type="button" onClick={deleteRgpdProspectFromSelection} className="px-4 py-2 rounded-[10px] text-sm font-semibold" style={{ background: "rgba(239,68,68,0.12)", color: "var(--do-ink)", border: "1px solid rgba(239,68,68,0.35)" }}>
                    Supprimer RGPD
                  </button>
                </div>
              </div>
            </div>

            <div className="grid xl:grid-cols-2 gap-6">
              <div className="rounded-[18px] p-5" style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}>
                <div className="label-eyebrow mb-3">Durées de conservation</div>
                <div className="space-y-2 text-sm" style={{ color: "var(--do-ink)" }}>
                  <div>Audit logs : {rgpdAdmin?.retention?.auditLogsMonths ?? 12} mois</div>
                  <div>Prospects inactifs : {rgpdAdmin?.retention?.prospectsInactiveMonths ?? 36} mois</div>
                  <div>Partages expirés : {rgpdAdmin?.retention?.expiredSharesMonths ?? 12} mois</div>
                  <div>Demandes RGPD : {rgpdAdmin?.retention?.rgpdRequestsYears ?? 5} ans</div>
                </div>
              </div>

              <div className="rounded-[18px] p-5" style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}>
                <div className="label-eyebrow mb-3">Actions RGPD disponibles</div>
                <div className="space-y-2 text-sm" style={{ color: "var(--do-ink)" }}>
                  <div>Exports, anonymisations et suppressions RGPD se font depuis les listes déroulantes ci-dessus.</div>
                  <div>Audit logs : {rgpd?.auditLogsCount ?? "—"}</div>
                  <div>Événements partages : {rgpd?.shareEventsCount ?? "—"}</div>
                </div>
              </div>
            </div>

            <div className="rounded-[18px] overflow-hidden" style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}>
              <div className="p-5">
                <div className="label-eyebrow mb-3">Audit sensible RGPD</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ color: "var(--do-muted)", borderBottom: "1px solid var(--do-border)" }}>
                        <th className="text-left py-2 pr-4">Date</th>
                        <th className="text-left py-2 pr-4">Action</th>
                        <th className="text-left py-2 pr-4">Tenant</th>
                        <th className="text-left py-2 pr-4">User</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rgpdAdmin?.sensitiveAudit?.items || []).map((item) => (
                        <tr key={item.id || `${item.createdAt}-${item.action}`} style={{ borderBottom: "1px solid var(--do-border)" }}>
                          <td className="py-3 pr-4 font-mono text-xs">{item.createdAt}</td>
                          <td className="py-3 pr-4 font-semibold">{item.action}</td>
                          <td className="py-3 pr-4 font-mono text-xs">{item.tenantId}</td>
                          <td className="py-3 pr-4 font-mono text-xs">{item.userId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === "accounts" && (
          <section className="space-y-6">
            <HistoryFilters section="accounts" />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={createUserFromPrompt}
                className="px-4 py-2 rounded-[10px] text-sm font-semibold"
                style={{ background: "var(--do-bg-solid)", color: "var(--do-ink)", border: "1px solid var(--do-border)" }}
                data-testid="platform-create-user"
              >
                Ajouter utilisateur
              </button>
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4">
              <MetricCard title="Tenants" value={accountSummary.tenants ?? "—"} icon={Database} />
              <MetricCard title="Admins" value={accountSummary.admins ?? "—"} icon={ShieldCheck} />
              <MetricCard title="Managers" value={accountSummary.managers ?? "—"} icon={Users} />
              <MetricCard title="Users" value={accountSummary.standardUsers ?? "—"} icon={Users} />
              <MetricCard title="SaaS Admins" value={accountSummary.saasAdmins ?? "—"} icon={ShieldCheck} />
            </div>

            <div className="rounded-[18px] overflow-hidden" style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}>
              <div className="p-5">
                <div className="label-eyebrow mb-3">Tenants</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ color: "var(--do-muted)", borderBottom: "1px solid var(--do-border)" }}>
                        <SortableTableHeader
                          label="Nom"
                          field="name"
                          sortBy={tableFilters.tenantsSortBy}
                          sortOrder={tableFilters.tenantsSortOrder}
                          onSort={(field) =>
                            toggleTableSort("tenants", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-tenants-sort-name"
                        />
                        <th className="text-left py-2 pr-4">TenantId</th>
                        <SortableTableHeader
                          label="Statut"
                          field="status"
                          sortBy={tableFilters.tenantsSortBy}
                          sortOrder={tableFilters.tenantsSortOrder}
                          onSort={(field) =>
                            toggleTableSort("tenants", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-tenants-sort-status"
                        />
                        <SortableTableHeader
                          label="Création"
                          field="createdAt"
                          sortBy={tableFilters.tenantsSortBy}
                          sortOrder={tableFilters.tenantsSortOrder}
                          onSort={(field) =>
                            toggleTableSort("tenants", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-tenants-sort-createdAt"
                        />
                        <th className="text-left py-2 pr-4">Users</th>
                        <th className="text-left py-2 pr-4">Docs</th>
                        <th className="text-left py-2 pr-4">Prospects</th>
                        <th className="text-left py-2 pr-4">Partages</th>
                        <th className="text-left py-2 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(tenantsHistory?.items || []).map((tenant) => (
                        <tr key={tenant.id} style={{ borderBottom: "1px solid var(--do-border)" }}>
                          <td className="py-3 pr-4 font-semibold">{tenant.name}</td>
                          <td className="py-3 pr-4 font-mono text-xs">{tenant.id}</td>
                          <td className="py-3 pr-4"><StatusBadge value={tenant.status} /></td>
                          <td className="py-3 pr-4 font-mono text-xs">{tenant.createdAt || "—"}</td>
                          <td className="py-3 pr-4">{tenant.usersCount}</td>
                          <td className="py-3 pr-4">{tenant.documentsCount}</td>
                          <td className="py-3 pr-4">{tenant.prospectsCount}</td>
                          <td className="py-3 pr-4">{tenant.sharesCount}</td>
                          <td className="py-3 pr-4">
                            {!tenant.isPlatformTenant && (
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className="text-xs font-semibold px-3 py-1.5 rounded-[8px]"
                                  style={{ border: "1px solid var(--do-border)" }}
                                  onClick={() => editTenantFromPrompt(tenant)}
                                  data-testid={`tenant-edit-${tenant.id}`}
                                >
                                  Modifier
                                </button>
                                <button
                                  type="button"
                                  className="text-xs font-semibold px-3 py-1.5 rounded-[8px]"
                                  style={{ border: "1px solid var(--do-border)" }}
                                  onClick={() => runAction(
                                    tenant.status === "suspended" ? "Réactiver ce tenant" : "Suspendre ce tenant",
                                    () => platform.updateTenantStatus(tenant.id, tenant.status === "suspended" ? "active" : "suspended")
                                  )}
                                  data-testid={`tenant-status-${tenant.id}`}
                                >
                                  {tenant.status === "suspended" ? "Réactiver" : "Suspendre"}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <PaginationControls data={tenantsHistory} pageKey="tenantsPage" />
            </div>

            <div className="rounded-[18px] overflow-hidden" style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}>
              <div className="p-5">
                <div className="label-eyebrow mb-3">Utilisateurs</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ color: "var(--do-muted)", borderBottom: "1px solid var(--do-border)" }}>
                        <th className="text-left py-2 pr-4">Nom</th>
                        <SortableTableHeader
                          label="Email"
                          field="email"
                          sortBy={tableFilters.usersSortBy}
                          sortOrder={tableFilters.usersSortOrder}
                          onSort={(field) =>
                            toggleTableSort("users", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-users-sort-email"
                        />
                        <th className="text-left py-2 pr-4">WhatsApp</th>
                        <SortableTableHeader
                          label="Rôle"
                          field="role"
                          sortBy={tableFilters.usersSortBy}
                          sortOrder={tableFilters.usersSortOrder}
                          onSort={(field) =>
                            toggleTableSort("users", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-users-sort-role"
                        />
                        <SortableTableHeader
                          label="Tenant"
                          field="tenantId"
                          sortBy={tableFilters.usersSortBy}
                          sortOrder={tableFilters.usersSortOrder}
                          onSort={(field) =>
                            toggleTableSort("users", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-users-sort-tenantId"
                        />
                        <SortableTableHeader
                          label="Statut"
                          field="status"
                          sortBy={tableFilters.usersSortBy}
                          sortOrder={tableFilters.usersSortOrder}
                          onSort={(field) =>
                            toggleTableSort("users", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-users-sort-status"
                        />
                        <th className="text-left py-2 pr-4">Sécurité</th>
                        <SortableTableHeader
                          label="Dernier login"
                          field="lastLoginAt"
                          sortBy={tableFilters.usersSortBy}
                          sortOrder={tableFilters.usersSortOrder}
                          onSort={(field) =>
                            toggleTableSort("users", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-users-sort-lastLoginAt"
                        />
                        <SortableTableHeader
                          label="Création"
                          field="createdAt"
                          sortBy={tableFilters.usersSortBy}
                          sortOrder={tableFilters.usersSortOrder}
                          onSort={(field) =>
                            toggleTableSort("users", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-users-sort-createdAt"
                        />
                        <th className="text-left py-2 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(usersHistory?.items || []).map((item) => (
                        <tr key={item.id} style={{ borderBottom: "1px solid var(--do-border)" }}>
                          <td className="py-3 pr-4">{item.firstName} {item.lastName}</td>
                          <td className="py-3 pr-4">{item.email}</td>
                          <td className="py-3 pr-4 font-mono text-xs">{item.phone || "—"}</td>
                          <td className="py-3 pr-4">{item.role}</td>
                          <td className="py-3 pr-4 font-mono text-xs">{item.tenantId}</td>
                          <td className="py-3 pr-4"><StatusBadge value={item.status} /></td>
                          <td className="py-3 pr-4">
                            {item.isLocked ? (
                              <div className="space-y-1">
                                <span className="text-xs font-semibold px-2 py-1 rounded-[8px]" style={{ border: "1px solid rgba(239,68,68,0.35)", color: "#DC2626" }}>
                                  Verrouillé
                                </span>
                                <div className="font-mono text-[11px]" style={{ color: "var(--do-muted)" }}>
                                  {item.lockedUntil || "—"}
                                </div>
                              </div>
                            ) : item.failedLoginAttempts > 0 ? (
                              <span className="font-mono text-xs" style={{ color: "var(--do-muted)" }}>
                                {item.failedLoginAttempts} échec(s)
                              </span>
                            ) : (
                              <span className="font-mono text-xs" style={{ color: "var(--do-muted)" }}>Normal</span>
                            )}
                          </td>
                          <td className="py-3 pr-4 font-mono text-xs">{item.lastLoginAt || "—"}</td>
                          <td className="py-3 pr-4 font-mono text-xs">{item.createdAt || "—"}</td>
                          <td className="py-3 pr-4">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="text-xs font-semibold px-3 py-1.5 rounded-[8px]"
                                style={{ border: "1px solid var(--do-border)" }}
                                onClick={() => editUserFromPrompt(item)}
                                data-testid={`user-edit-${item.id}`}
                              >
                                Modifier
                              </button>

                              <button
                                type="button"
                                className="text-xs font-semibold px-3 py-1.5 rounded-[8px]"
                                style={{ border: "1px solid var(--do-border)" }}
                                onClick={() => resetPasswordFromPrompt(item)}
                                data-testid={`user-reset-password-${item.id}`}
                              >
                                Reset MDP
                              </button>

                              {item.isLocked && (
                                <button
                                  type="button"
                                  className="text-xs font-semibold px-3 py-1.5 rounded-[8px]"
                                  style={{ border: "1px solid var(--do-border)" }}
                                  onClick={() => runAction("Déverrouiller ce compte", () => platform.unlockUser(item.id))}
                                  data-testid={`user-unlock-${item.id}`}
                                >
                                  Déverrouiller
                                </button>
                              )}

                              {item.role !== "saas_admin" && (
                                <button
                                  type="button"
                                  className="text-xs font-semibold px-3 py-1.5 rounded-[8px]"
                                  style={{ border: "1px solid var(--do-border)" }}
                                  onClick={() => runAction(
                                    item.status === "disabled" ? "Réactiver ce compte" : "Désactiver ce compte",
                                    () => platform.updateUserStatus(item.id, item.status === "disabled" ? "active" : "disabled")
                                  )}
                                  data-testid={`user-status-${item.id}`}
                                >
                                  {item.status === "disabled" ? "Réactiver" : "Désactiver"}
                                </button>
                              )}

                              {item.role !==
                                "saas_admin" && (
                                <button
                                  type="button"
                                  className="text-xs font-semibold px-3 py-1.5 rounded-[8px]"
                                  style={{
                                    border:
                                      "1px solid var(--do-border)",
                                  }}
                                  onClick={() => {
                                    const role =
                                      window.prompt(
                                        "Nouveau rôle : admin, manager, user",
                                        item.role
                                      );

                                    if (!role) return;

                                    runAction(
                                      `Changer le rôle en ${role}`,
                                      () =>
                                        platform.updateUserRole(
                                          item.id,
                                          role
                                        )
                                    );
                                  }}
                                  data-testid={
                                    `user-role-${item.id}`
                                  }
                                >
                                  Changer rôle
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <PaginationControls data={usersHistory} pageKey="usersPage" />
            </div>
          </section>
        )}

        {activeTab === "support" && (
          <PlatformSupport initialTicketId={searchParams.get("ticket") || ""} />
        )}

        {activeTab === "logs" && (
          <section className="space-y-6">
            <HistoryFilters section="logs" />

            <div
              className="rounded-[18px] overflow-hidden"
              style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)" }}
            >
              <div className="p-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="label-eyebrow">Logs / Audit</div>
                  <button
                    type="button"
                    onClick={exportLogsCsv}
                    className="px-3 py-2 rounded-[10px] text-xs font-semibold"
                    style={{ background: "var(--do-bg-solid)", border: "1px solid var(--do-border)", color: "var(--do-ink)" }}
                    data-testid="platform-export-logs-csv"
                  >
                    Export CSV
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ color: "var(--do-muted)", borderBottom: "1px solid var(--do-border)" }}>
                        <SortableTableHeader
                          label="Date"
                          field="createdAt"
                          sortBy={tableFilters.logsSortBy}
                          sortOrder={tableFilters.logsSortOrder}
                          onSort={(field) =>
                            toggleTableSort("logs", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-logs-sort-createdAt"
                        />
                        <SortableTableHeader
                          label="Action"
                          field="action"
                          sortBy={tableFilters.logsSortBy}
                          sortOrder={tableFilters.logsSortOrder}
                          onSort={(field) =>
                            toggleTableSort("logs", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-logs-sort-action"
                        />
                        <SortableTableHeader
                          label="Tenant"
                          field="tenantId"
                          sortBy={tableFilters.logsSortBy}
                          sortOrder={tableFilters.logsSortOrder}
                          onSort={(field) =>
                            toggleTableSort("logs", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-logs-sort-tenantId"
                        />
                        <SortableTableHeader
                          label="User"
                          field="userId"
                          sortBy={tableFilters.logsSortBy}
                          sortOrder={tableFilters.logsSortOrder}
                          onSort={(field) =>
                            toggleTableSort("logs", field)
                          }
                          className="text-left py-2 pr-4 font-bold"
                          testId="supervision-logs-sort-userId"
                        />
                      </tr>
                    </thead>

                    <tbody>
                      {(logsHistory?.items || []).map((item) => (
                        <tr key={item.id || `${item.createdAt}-${item.action}`} style={{ borderBottom: "1px solid var(--do-border)" }}>
                          <td className="py-3 pr-4 font-mono text-xs">{item.createdAt}</td>
                          <td className="py-3 pr-4 font-semibold">{item.action}</td>
                          <td className="py-3 pr-4 font-mono text-xs">{item.tenantId}</td>
                          <td className="py-3 pr-4 font-mono text-xs">{item.userId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <PaginationControls data={logsHistory} pageKey="logsPage" />
            </div>
          </section>
        )}

        {activeTab === "profile" && (
          <section className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard title="Email" value={user?.email || "—"} icon={ShieldCheck} />
            <MetricCard title="Rôle" value={user?.role || "—"} icon={Users} />
            <MetricCard title="Tenant technique" value={user?.tenantId || "—"} icon={Database} />
            <MetricCard title="Sécurité" value="SaaS Admin" subtitle="Accès plateforme uniquement" icon={ShieldCheck} />
          </section>
        )}
      </section>
    </main>
  );
}
